// RUTA: src/components/fitness/DailyBriefingCard.tsx
// Panel "asistente" en el home del cliente — lo PRIMERO que ve al
// abrir la app. Es el saludo que marca el tono del día.
//
// Diseño con foco en impacto visual:
//   - Foto de perfil grande + saludo personalizado a la derecha
//   - Emoji 👋 con wave continuo
//   - Foto con glow-ring pulsante
//   - Saludo en cascada palabra por palabra
//   - Mensaje motivacional con fade-in slow después del saludo
//   - Gradient animado de fondo
//   - Racha con contador animado (sube 0 → N al cargar)
//   - 3 chips primarios con hover lift
//   - Recordatorios condicionales

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dumbbell, Utensils, Flame, Camera, Scale, Activity, Bell, Loader2, Sparkles } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import {
  greeting, dayOfWeekEs, calcStreak, motivationalMessage,
  type DailyContext,
} from '@/lib/dailyBriefing';
import { getTodayLocalString } from '@/lib/dateUtils';

interface Props {
  /** User ID para el avatar editable y para todas las queries. */
  userId: string | undefined;
  /** Nombre del cliente para el saludo. */
  displayName: string;
  /** Función para hacer scroll a una sección del home. */
  onNavigate: (sectionId: string) => void;
  /** Meta diaria de calorías. */
  dailyCalorieGoal?: number | null;
  /** Meta diaria de cardio en kcal (si > 0, recordatorio aplica). */
  dailyCardioGoal?: number | null;
  /** ¿Es el perfil propio? Habilita edición de foto. */
  isOwnProfile?: boolean;
}

interface ChipState {
  routineLabel: string;
  routineCount: number;
  routineCompleted: boolean;
  mealsPlanned: number;
  mealsLogged: number;
  caloriesToday: number;
}

interface Reminder {
  id: string;
  icon: typeof Bell;
  text: string;
  targetSection: string;
}

const scrollTo = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/** Hook simple para animar un contador de 0 al valor objetivo. */
function useCountUp(target: number, durationMs = 900): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target <= 0) { setValue(0); return; }
    const start = performance.now();
    let raf: number;
    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
}

export function DailyBriefingCard({
  userId,
  displayName,
  onNavigate,
  dailyCalorieGoal,
  dailyCardioGoal,
  isOwnProfile = true,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(0);
  const [chips, setChips] = useState<ChipState>({
    routineLabel: '—', routineCount: 0, routineCompleted: false,
    mealsPlanned: 0, mealsLogged: 0, caloriesToday: 0,
  });
  const [reminders, setReminders] = useState<Reminder[]>([]);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancel = false;
    (async () => {
      const todayLocal = getTodayLocalString();
      const dayOfWeek = dayOfWeekEs();

      const [
        streakValue,
        { data: routines },
        { data: meals },
        { data: foodLogs },
        { data: workoutToday },
        { data: cardioToday },
        { data: latestPhoto },
        { data: latestWeight },
      ] = await Promise.all([
        calcStreak(userId),
        supabase.from('client_routines')
          .select('muscle_group, day_name')
          .eq('client_id', userId).eq('is_active', true)
          .ilike('day_name', dayOfWeek),
        supabase.from('client_meal_plans')
          .select('meal_time, day_of_week, plan_mode')
          .eq('client_id', userId).eq('is_active', true),
        supabase.from('food_logs')
          .select('id, calories, meal_type')
          .eq('user_id', userId).eq('log_date', todayLocal),
        supabase.from('workout_session')
          .select('id')
          .eq('user_id', userId).eq('session_date', todayLocal).limit(1),
        supabase.from('exercise_logs')
          .select('id')
          .eq('user_id', userId).eq('log_date', todayLocal)
          .ilike('exercise_type', '%cardio%').limit(1),
        supabase.from('progress_photos')
          .select('created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('body_measurements')
          .select('measure_date')
          .eq('user_id', userId)
          .order('measure_date', { ascending: false }).limit(1).maybeSingle(),
      ]);

      if (cancel) return;

      const routineCount = (routines as any[] | null)?.length ?? 0;
      const muscleGroups = Array.from(new Set(
        (routines as any[] | null)?.map(r => (r as any).muscle_group as string).filter(Boolean) ?? []
      ));
      const routineLabel = routineCount === 0
        ? 'Día de descanso'
        : muscleGroups.join(', ') || 'Tu rutina';
      const routineCompleted = ((workoutToday as any[] | null)?.length ?? 0) > 0;

      const mealList = (meals as any[] | null) ?? [];
      const planMode = mealList[0]?.plan_mode || 'time';
      const todayMeals = planMode === 'day'
        ? mealList.filter(m => m.day_of_week === dayOfWeek || !m.day_of_week)
        : mealList;
      // Contamos meal_times DISTINTOS — el plan puede tener varias
      // filas por meal_time (legacy con opciones) y un meal registrado
      // puede tener varios items (ej. desayuno = huevos + frijoles +
      // café son 3 food_logs del mismo meal_type 'desayuno').
      const mealsPlanned = new Set(
        todayMeals.map((m: any) => m.meal_time as string).filter(Boolean)
      ).size;
      const mealsLogged = new Set(
        ((foodLogs as any[] | null) ?? [])
          .map(f => (f as any).meal_type as string)
          .filter(Boolean)
      ).size;

      const caloriesToday = ((foodLogs as any[] | null) ?? [])
        .reduce((s, r) => s + (Number((r as any).calories) || 0), 0);

      const newReminders: Reminder[] = [];

      if ((dailyCardioGoal ?? 0) > 0 && ((cardioToday as any[] | null)?.length ?? 0) === 0) {
        newReminders.push({
          id: 'cardio',
          icon: Activity,
          text: 'Aún no haces tu cardio del día',
          targetSection: 'cardio',
        });
      }

      const photoDate = (latestPhoto as any)?.created_at;
      const daysSincePhoto = photoDate
        ? Math.floor((Date.now() - new Date(photoDate).getTime()) / 86_400_000)
        : Infinity;
      if (daysSincePhoto > 30) {
        newReminders.push({
          id: 'photo',
          icon: Camera,
          text: daysSincePhoto === Infinity
            ? 'Toma tu primera foto de progreso'
            : `No has tomado tu foto de progreso en ${daysSincePhoto} días`,
          targetSection: 'progreso-visual',
        });
      }

      const weightDate = (latestWeight as any)?.measure_date;
      const daysSinceWeight = weightDate
        ? Math.floor((Date.now() - new Date(weightDate + 'T12:00:00').getTime()) / 86_400_000)
        : Infinity;
      if (daysSinceWeight > 7) {
        newReminders.push({
          id: 'weight',
          icon: Scale,
          text: daysSinceWeight === Infinity
            ? 'Registra tu primer peso'
            : `Tu peso lleva ${daysSinceWeight} días sin actualizar`,
          targetSection: 'evaluacion',
        });
      }

      setStreak(streakValue);
      setChips({
        routineLabel,
        routineCount,
        routineCompleted,
        mealsPlanned,
        mealsLogged,
        caloriesToday,
      });
      setReminders(newReminders);
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, [userId, dailyCalorieGoal, dailyCardioGoal]);

  // Mensaje motivacional contextual
  const motivationalText = useMemo(() => {
    const ctx: DailyContext = {
      streak,
      caloriesPct: dailyCalorieGoal && dailyCalorieGoal > 0
        ? (chips.caloriesToday / dailyCalorieGoal) * 100
        : 0,
      isRestDay: chips.routineCount === 0,
      zeroToday: chips.mealsLogged === 0 && !chips.routineCompleted,
      hour: new Date().getHours(),
    };
    return motivationalMessage(ctx);
  }, [streak, chips, dailyCalorieGoal]);

  const animatedStreak = useCountUp(streak, 1200);

  if (loading) {
    return (
      <section className="rounded-2xl bg-card border border-border p-6 flex items-center justify-center min-h-[160px]">
        <Loader2 className="w-5 h-5 text-accent animate-spin" />
      </section>
    );
  }

  // Color del chip de calorías según porcentaje
  const caloriesPct = dailyCalorieGoal && dailyCalorieGoal > 0
    ? (chips.caloriesToday / dailyCalorieGoal) * 100
    : 0;
  const caloriesRemaining = dailyCalorieGoal ? Math.max(0, dailyCalorieGoal - chips.caloriesToday) : 0;
  const caloriesTone =
    caloriesPct >= 80 && caloriesPct <= 110 ? 'text-green-600 dark:text-green-400' :
    caloriesPct > 110 ? 'text-amber-600 dark:text-amber-400' :
    'text-foreground';

  // Saludo en palabras (para cascada)
  const firstName = displayName.split(' ')[0] || 'tú';
  const greetingWords = [greeting(), 'días' /* placeholder reemplazado abajo */];
  // Construcción correcta: separamos saludo + nombre + emoji para
  // animar cada parte con delay distinto.
  const greetingText = greeting();
  const greetingParts = greetingText.split(' '); // "Buenos" "días"

  return (
    <section className="relative overflow-hidden rounded-2xl bg-card dark:bg-primary shadow-lg dark:shadow-2xl border border-border dark:border-transparent noise-overlay">
      {/* Gradient overlay — industrial en dark, sutil en light */}
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-card to-accent/5 dark:from-primary dark:via-primary/80 dark:to-accent/20 z-0"></div>
      {/* Carbon fibre texture — solo visible en dark */}
      <div className="absolute inset-0 opacity-0 dark:opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] z-0"></div>
      {/* Radial glow accent — más sutil en light */}
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[400px] h-[400px] bg-accent/5 dark:bg-accent/10 rounded-full blur-[120px] z-0"></div>

      <div className="relative z-10 p-5 md:p-7">
      {/* Header: foto + saludo */}
      <div className="flex items-center gap-4 mb-4">
        {userId && (
          <div className="relative shrink-0 rounded-full animate-glow-ring">
            <UserAvatar
              userId={userId}
              name={displayName}
              size="lg"
              editable={isOwnProfile}
            />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="text-lg md:text-2xl font-black tracking-tight leading-tight text-foreground dark:text-primary-foreground">
            {/* Saludo en cascada palabra por palabra */}
            {greetingParts.map((word, i) => (
              <span
                key={`g-${i}`}
                className="inline-block opacity-0 animate-rise-in mr-1.5"
                style={{ animationDelay: `${i * 120}ms` }}
              >
                {word}
              </span>
            ))}
            <span
              className="inline-block opacity-0 animate-rise-in mr-1.5"
              style={{ animationDelay: `${greetingParts.length * 120}ms` }}
            >
              ,
            </span>
            <span
              className="inline-block opacity-0 animate-rise-in text-accent mr-1.5"
              style={{ animationDelay: `${(greetingParts.length + 1) * 120}ms` }}
            >
              {firstName}
            </span>
            <span
              className="inline-block opacity-0 animate-rise-in"
              style={{ animationDelay: `${(greetingParts.length + 2) * 120}ms`, transformOrigin: '70% 70%' }}
            >
              <span className="inline-block animate-wave origin-bottom-right">👋</span>
            </span>
          </h2>
          <p
            className="text-xs md:text-sm text-muted-foreground dark:text-primary-foreground/70 mt-1 leading-relaxed opacity-0 animate-rise-in"
            style={{ animationDelay: '700ms', animationDuration: '1s' }}
          >
            {motivationalText}
          </p>
          {streak >= 3 && (
            <div
              className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-[11px] font-black uppercase tracking-wider text-amber-700 dark:text-amber-400 opacity-0 animate-rise-in"
              style={{ animationDelay: '900ms', animationDuration: '1s' }}
            >
              <Sparkles className="w-3 h-3" />
              <span>{animatedStreak} días seguidos</span>
            </div>
          )}
        </div>
      </div>

      {/* Chips primarios */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Chip
          icon={Dumbbell}
          label="Rutina hoy"
          title={chips.routineLabel}
          sub={
            chips.routineCount === 0
              ? '💤 Descansa'
              : chips.routineCompleted
                ? <span className="text-green-600 dark:text-green-400">✅ Completada</span>
                : `${chips.routineCount} ejercicio${chips.routineCount === 1 ? '' : 's'}`
          }
          onClick={() => scrollTo('rutina')}
          delay={1100}
        />
        <Chip
          icon={Utensils}
          label="Comidas"
          title={`${chips.mealsLogged} de ${chips.mealsPlanned || '—'}`}
          sub={
            chips.mealsPlanned === 0
              ? 'Sin plan asignado'
              : chips.mealsLogged >= chips.mealsPlanned
                ? <span className="text-green-600 dark:text-green-400">✅ Completas</span>
                : `Faltan ${chips.mealsPlanned - chips.mealsLogged}`
          }
          onClick={() => scrollTo('plan')}
          delay={1250}
        />
        <Chip
          icon={Flame}
          label="Calorías"
          title={
            <span className={caloriesTone}>
              {Math.round(chips.caloriesToday)}
              <span className="text-xs text-muted-foreground font-normal">
                {dailyCalorieGoal ? ` / ${dailyCalorieGoal}` : ''}
              </span>
            </span>
          }
          sub={
            !dailyCalorieGoal
              ? 'Sin meta definida'
              : caloriesPct > 110
                ? `Pasaste por ${Math.round(chips.caloriesToday - dailyCalorieGoal)} kcal`
                : `Faltan ${Math.round(caloriesRemaining)} kcal`
          }
          onClick={() => scrollTo('calorias')}
          delay={1400}
        />
      </div>

      {/* Recordatorios secundarios condicionales */}
      {reminders.length > 0 && (
        <div
          className="space-y-1.5 mt-4 opacity-0 animate-rise-in"
          style={{ animationDelay: '1600ms', animationDuration: '1s' }}
        >
          <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-muted-foreground dark:text-primary-foreground/50">
            <Bell className="w-3 h-3" />
            Recordatorios
          </div>
          {reminders.map(r => {
            const Icon = r.icon;
            return (
              <button
                key={r.id}
                onClick={() => scrollTo(r.targetSection)}
                className="w-full flex items-center gap-2 text-left text-xs bg-amber-500/10 border border-amber-500/30 hover:border-amber-500/50 hover:-translate-y-0.5 rounded-lg px-3 py-2 transition-all duration-200"
              >
                <Icon className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <span className="flex-1 text-amber-700 dark:text-amber-200">{r.text}</span>
                <span className="text-[10px] text-amber-600 dark:text-amber-400 font-bold">→</span>
              </button>
            );
          })}
        </div>
      )}
      </div>
    </section>
  );
}

interface ChipProps {
  icon: typeof Dumbbell;
  label: string;
  title: React.ReactNode;
  sub: React.ReactNode;
  onClick: () => void;
  delay: number;
}

function Chip({ icon: Icon, label, title, sub, onClick, delay }: ChipProps) {
  return (
    <button
      onClick={onClick}
      className="text-left bg-background/60 dark:bg-white/5 hover:bg-background/80 dark:hover:bg-white/10 backdrop-blur-sm border border-border dark:border-white/10 hover:border-accent/50 rounded-xl p-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20 opacity-0 animate-rise-in"
      style={{ animationDelay: `${delay}ms`, animationDuration: '0.8s' }}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-7 h-7 rounded-lg bg-accent/20 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5 text-accent" />
        </div>
        <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground dark:text-primary-foreground/50">{label}</span>
      </div>
      <div className="text-sm font-black text-foreground dark:text-primary-foreground">{title}</div>
      <div className="text-[11px] text-muted-foreground dark:text-primary-foreground/60 mt-0.5">{sub}</div>
    </button>
  );
}
